import { type ReactNode, useEffect, useCallback } from "react";
import { X } from "lucide-react";

type DrawerProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly title: string;
  /** Optional line under the title (e.g. panel description). */
  readonly subtitle?: string;
  /** Tailwind classes for the sliding panel width (default `max-w-md`). */
  readonly panelClassName?: string;
  readonly className?: string;
  readonly headerIcon?: ReactNode;
  readonly headerIconContainerClassName?: string;
  readonly children: ReactNode;
};

export const Drawer = ({
  isOpen,
  onClose,
  title,
  subtitle,
  panelClassName = "max-w-md",
  className = "px-6 py-6",
  headerIcon,
  headerIconContainerClassName,
  children,
}: DrawerProps) => {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div
        className="fixed inset-0 bg-black/40 transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      <dialog
        open
        aria-modal="true"
        aria-label={title}
        className={`relative z-10 m-0 flex h-full w-full flex-col bg-white shadow-xl transition-transform duration-300 ease-in-out ${panelClassName}`}
      >
        <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            {headerIcon && (
              <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600 ${headerIconContainerClassName ?? ""}`}
              >
                {headerIcon}
              </div>
            )}
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
              {subtitle ? (
                <p className="mt-1 text-sm font-normal text-gray-500">{subtitle}</p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close drawer"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className={`flex-1 overflow-y-auto ${className}`}>{children}</div>
      </dialog>
    </div>
  );
};
